import { knowledgeService } from './knowledgeService';
import type { LearningPositionCoverageStat, LearningPositionSpineGraphNode } from './reviewService';
import { getSubjectValueBindingRecord } from '../utils/subjectValueBinding';
import { type SubjectCode } from '../utils/subjectConfig';

const SUBJECT_QUERY_LABEL: Record<SubjectCode, string> = {
  yuwen: '语文',
  math: '数学',
  english: '英语',
  other: '其他',
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
}

function readText(record: Record<string, unknown> | undefined, keys: string[]): string {
  if (!record) {
    return '';
  }
  for (const key of keys) {
    const value = String(record[key] ?? '').trim();
    if (value) {
      return value;
    }
  }
  return '';
}

function normalizeLookup(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '');
}

function toCoveragePercent(covered: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.min(100, Math.round((covered / total) * 100));
}

export function coverageStatToPercent(stat: LearningPositionCoverageStat | null | undefined): number | null {
  if (!stat) {
    return null;
  }
  if (Number.isFinite(stat.rate) && stat.rate >= 0 && stat.rate <= 1) {
    return Math.min(100, Math.round(stat.rate * 100));
  }
  return toCoveragePercent(stat.covered, stat.total);
}

export function formatCoverageHeadline(percent: number | null | undefined, loading: boolean): string {
  if (loading || percent == null || !Number.isFinite(percent)) {
    return '--%';
  }
  return `${Math.max(0, Math.min(100, Math.round(percent)))}%`;
}

/** Client fallback when AppFunctor omits uploadedTrunkCoverage. */
export async function computeUploadedTrunkCoverageFallback(
  userId: string,
  subject: SubjectCode,
  nodes: LearningPositionSpineGraphNode[],
): Promise<LearningPositionCoverageStat> {
  const total = nodes.length;
  if (total <= 0) {
    return { covered: 0, total: 0, rate: 0 };
  }

  const nodeIds = new Set(nodes.map((node) => node.nodeId));
  const titleToId = new Map<string, string>();
  for (const node of nodes) {
    const title = normalizeLookup(node.title);
    if (title) {
      titleToId.set(title, node.nodeId);
    }
  }
  const covered = new Set<string>();
  const pageSize = 100;
  let page = 1;

  while (page <= 40) {
    const result = await knowledgeService.listKnowledgeParticles({
      userId,
      subject: SUBJECT_QUERY_LABEL[subject],
      page,
      pageSize,
    });

    for (const particle of result.data || []) {
      const metadata = asRecord(particle.metadata);
      const binding = getSubjectValueBindingRecord(metadata);
      if (!binding) {
        continue;
      }

      const nodeId = readText(binding, ['spineNodeId']);
      if (nodeId && nodeIds.has(nodeId)) {
        covered.add(nodeId);
        continue;
      }

      const titleHint = normalizeLookup(
        readText(binding, ['spineNodeTitle', 'candidateSpineNodeTitle']),
      );
      const matched = titleHint ? titleToId.get(titleHint) : undefined;
      if (matched) {
        covered.add(matched);
      }
    }

    const listed = result.data?.length ?? 0;
    const listedTotal = result.pagination?.total ?? 0;
    if (listed < pageSize || page * pageSize >= listedTotal) {
      break;
    }
    page += 1;
  }

  return {
    covered: covered.size,
    total,
    rate: total > 0 ? Math.round((covered.size / total) * 1000) / 1000 : 0,
  };
}
